import React, { useState } from 'react';
import VerificationCodeModal from './VerificationCodeModal';
import CompanyLogo from '../../../../components/ui/CompanyLogo';

interface IDVerificationPageProps {
  companyName: string;
  companyLogo: string;
  location: string;
  onWatchVideo: () => void;
  onBack: () => void;
  onConfirm: (data: IDVerificationData) => void;
  // In production, this would be populated from OCR of the ID photo
  initialData?: Partial<IDVerificationData>;
}

export interface IDVerificationData {
  fullName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
  phoneNumber: string;
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function IDVerificationPage({
  companyName,
  companyLogo,
  location,
  onWatchVideo,
  onBack,
  onConfirm,
  initialData = {}
}: IDVerificationPageProps) {
  // Form state (pre-populated with mock data from "scanned" ID)
  const [formData, setFormData] = useState<IDVerificationData>({
    fullName: initialData.fullName || 'Jane Doe',
    addressLine1: initialData.addressLine1 || '1234 Market St',
    addressLine2: initialData.addressLine2 || 'APT 567',
    city: initialData.city || 'St. Louis',
    state: initialData.state || 'MO',
    zipCode: initialData.zipCode || '63101',
    email: initialData.email || 'janedoe11@gmail.com',
    phoneNumber: initialData.phoneNumber || '(347) 123-4567',
  });

  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const handleInputChange = (field: keyof IDVerificationData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleVerifyPhone = () => {
    // In production, this would send a verification code via SMS
    setShowVerificationModal(true);
  };

  const handleVerificationBack = () => {
    setShowVerificationModal(false);
  };

  const handleVerificationSubmit = (code: string) => {
    // Demo: Accept "1234" as the correct code
    if (code === '1234') {
      setPhoneVerified(true);
      setShowVerificationModal(false);
    } else {
      alert('Invalid code. Please try again. (Use 1234 for demo)');
    }
  };

  const handleSubmit = () => {
    // Basic validation
    if (!formData.fullName || !formData.addressLine1 || !formData.city || 
        !formData.state || !formData.zipCode || !formData.email || !formData.phoneNumber) {
      alert('Please fill in all required fields');
      return;
    }
    onConfirm(formData);
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* HEADER (reused from other screens) */}
      <div className="flex-shrink-0 bg-white px-5 pt-[14px]">
        {/* Logo, Company Name, Location, Watch Video */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start gap-2">
            {/* Company Logo */}
            <CompanyLogo
              src={companyLogo}
              alt={companyName}
              className="w-[57px] h-[57px] rounded"
            />
            
            {/* Company Info */}
            <div className="flex flex-col justify-center">
              <h1 className="text-[19px] font-bold text-[#28292d]">
                {companyName}
              </h1>
              <p className="text-[17px] font-normal text-[#28292d]">
                {location}
              </p>
            </div>
          </div>

          {/* Watch Video Link */}
          <button
            onClick={onWatchVideo}
            className="flex items-center gap-1 self-center"
          >
            <svg
              className="w-3 h-3"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2 1.5L10 6L2 10.5V1.5Z"
                fill="#0156FF"
                stroke="#0156FF"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-[16px] font-normal text-[#0156FF]">
              Watch Video
            </span>
          </button>
        </div>

        {/* Gray Divider */}
        <div className="h-px bg-[#b9babf] mx-4" />
      </div>

      {/* SCROLLABLE FORM CONTENT */}
      <div className="flex-1 overflow-y-auto px-4 pt-6 pb-20">
        {/* Heading */}
        <h2 className="text-[16px] font-bold text-[#28292d] mb-6">
          Confirm your ID information
        </h2>

        {/* Full Name */}
        <div className="mb-5">
          <label className="block text-[14px] font-bold text-[#28292d] mb-2">
            Full Name
          </label>
          <input
            type="text"
            value={formData.fullName}
            onChange={(e) => handleInputChange('fullName', e.target.value)}
            className="w-full h-[40px] px-3 border border-[#b9babf] rounded text-[14px] text-[#28292d] focus:outline-none focus:border-[#0156FF]"
          />
        </div>

        {/* Address Row 1 */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <label className="block text-[14px] font-bold text-[#28292d] mb-2">
              Address Line 1
            </label>
            <input
              type="text"
              value={formData.addressLine1}
              onChange={(e) => handleInputChange('addressLine1', e.target.value)}
              className="w-full h-[40px] px-3 border border-[#b9babf] rounded text-[14px] text-[#28292d] focus:outline-none focus:border-[#0156FF]"
            />
          </div>
          <div>
            <label className="block text-[14px] font-bold text-[#28292d] mb-2">
              Address Line 2
            </label>
            <input
              type="text"
              value={formData.addressLine2}
              onChange={(e) => handleInputChange('addressLine2', e.target.value)}
              className="w-full h-[40px] px-3 border border-[#b9babf] rounded text-[14px] text-[#28292d] focus:outline-none focus:border-[#0156FF]"
            />
          </div>
        </div>

        {/* City, State, Zip Code Row */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <div>
            <label className="block text-[14px] font-bold text-[#28292d] mb-2">
              City
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className="w-full h-[40px] px-3 border border-[#b9babf] rounded text-[14px] text-[#28292d] focus:outline-none focus:border-[#0156FF]"
            />
          </div>
          <div>
            <label className="block text-[14px] font-bold text-[#28292d] mb-2">
              State
            </label>
            <select
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              className="w-full h-[40px] px-3 border border-[#b9babf] rounded text-[14px] text-[#28292d] focus:outline-none focus:border-[#0156FF] bg-white"
            >
              {US_STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[14px] font-bold text-[#28292d] mb-2">
              Zip Code
            </label>
            <input
              type="text"
              value={formData.zipCode}
              onChange={(e) => handleInputChange('zipCode', e.target.value)}
              maxLength={5}
              className="w-full h-[40px] px-3 border border-[#b9babf] rounded text-[14px] text-[#28292d] focus:outline-none focus:border-[#0156FF]"
            />
          </div>
        </div>

        {/* Contact Information Section */}
        <h3 className="text-[16px] font-bold text-[#28292d] mb-5">
          Contact Information
        </h3>

        {/* Email Address */}
        <div className="mb-5">
          <label className="block text-[14px] font-bold text-[#28292d] mb-2">
            Email Address
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full h-[40px] px-3 border border-[#b9babf] rounded text-[14px] text-[#28292d] focus:outline-none focus:border-[#0156FF]"
          />
        </div>

        {/* Phone Number with Verify Button */}
        <div className="mb-5">
          <label className="block text-[14px] font-bold text-[#28292d] mb-2">
            Phone Number
          </label>
          <div className="flex gap-2">
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              className="flex-1 h-[40px] px-3 border border-[#b9babf] rounded text-[14px] text-[#28292d] focus:outline-none focus:border-[#0156FF]"
              style={{ maxWidth: 'calc(100% - 90px)' }}
            />
            <button
              onClick={handleVerifyPhone}
              disabled={phoneVerified}
              className={`h-[40px] rounded-full text-[16px] font-bold whitespace-nowrap ${
                phoneVerified 
                  ? 'bg-[#12b71a] text-white px-5' 
                  : 'bg-[#8E8E8F] text-white px-6 active:scale-[0.98] transition-transform'
              }`}
              style={{ minWidth: phoneVerified ? '80px' : '65px' }}
            >
              {phoneVerified ? '✓ Verified' : 'Verify'}
            </button>
          </div>
        </div>
      </div>

      {/* FIXED BOTTOM BUTTONS */}
      <div className="fixed bottom-0 left-0 right-0 px-5 py-4 flex items-center justify-between bg-white border-t border-[#eeeeee] z-50">
        <button
          onClick={onBack}
          className="bg-[#8E8E8F] text-white text-[16px] font-bold rounded-full active:scale-[0.98] transition-transform flex items-center justify-center"
          style={{ width: '90px', height: '40px' }}
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          className="bg-[#0156FF] text-white text-[16px] font-bold rounded-full active:scale-[0.98] transition-transform flex items-center justify-center"
          style={{ width: '90px', height: '40px' }}
        >
          Confirm
        </button>
      </div>

      {/* VERIFICATION CODE MODAL */}
      {showVerificationModal && (
        <VerificationCodeModal
          phoneNumber={formData.phoneNumber}
          onBack={handleVerificationBack}
          onVerify={handleVerificationSubmit}
        />
      )}
    </div>
  );
}

